import { fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';

export type OpponentCandidate = {
  userId?: string;
  petId?: string;
  customName?: string;
};

type Options = {
  onlyOwnedPets?: boolean;
  excludeUserId?: string | null;
};

function pickRandom<T>(arr: T[]): T | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const idx = Math.floor(Math.random() * arr.length);
  return arr[idx] ?? null;
}

async function pickFromPool(): Promise<OpponentCandidate | null> {
  try {
    const poolRef = fuego.db.doc(
      `${CollectionPath.PETSETTING}/opponentPetsPool`,
    );
    const snap = await poolRef.get();
    const items = (snap.data()?.items || []) as Array<{
      userId: string;
      petId: string;
      name: string;
    }>;
    if (!Array.isArray(items) || items.length === 0) return null;
    const item = pickRandom(items);
    if (!item) return null;
    return { userId: item.userId, petId: item.petId, customName: item.name };
  } catch (e) {
    return null;
  }
}

async function pickFromOwnedPets(
  opts: Options,
): Promise<OpponentCandidate | null> {
  try {
    // 랜덤 커서 기법
    const x = Math.random();
    const base = fuego.db.collection('owned_pets').where('hasName', '==', true);
    const snap = await base.orderBy('rand').startAt(x).limit(1).get();
    let doc = snap.docs[0];
    if (!doc) {
      const snap2 = await base
        .orderBy('rand', 'desc')
        .startAt(x)
        .limit(1)
        .get();
      doc = snap2.docs[0];
    }
    if (!doc) return null;
    const v = doc.data() as any;
    if (opts.excludeUserId && v.userId === opts.excludeUserId) return null;
    return { userId: v.userId, petId: v.petId, customName: v.name };
  } catch (e) {
    return null;
  }
}

async function pickFromCollectionGroup(
  opts: Options,
): Promise<OpponentCandidate | null> {
  try {
    if (!opts.onlyOwnedPets) return null; // 조건이 꺼져 있으면 스킵
    const snap = await fuego.db
      .collectionGroup('pets')
      .orderBy('createdAt', 'desc')
      .limit(20)
      .get();
    const docs: Array<{ id: string; userId?: string; customName?: string }> =
      snap.docs
        .map((d: any) => ({ id: d.id, ...(d.data() || {}) }))
        .filter(
          (p: any) =>
            p &&
            p.customName &&
            (!opts.excludeUserId || p.userId !== opts.excludeUserId),
        );
    const picked = pickRandom(docs);
    if (!picked) return null;
    return {
      userId: picked.userId,
      petId: picked.id,
      customName: picked.customName,
    };
  } catch (e) {
    return null;
  }
}

// 두 단계 풀 전략: 캐시 풀 → owned_pets → collectionGroup 폴백
export async function pickOpponentPetCandidate(
  options: Options = {},
): Promise<OpponentCandidate | null> {
  const fromPool = await pickFromPool();
  if (fromPool) return fromPool;

  const fromOwned = await pickFromOwnedPets(options);
  if (fromOwned) return fromOwned;

  const fromGroup = await pickFromCollectionGroup(options);
  if (fromGroup) return fromGroup;

  return null;
}

export default pickOpponentPetCandidate;
